import { FC, type MouseEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, Stack, type Theme, Typography } from '@mui/material';

import { Checkbox } from '@mopla/ui';

import type { IOperatorLegalInfo } from './utils';

interface ITosAcceptanceProps {
	/**
	 * true → interactive checkbox + first-person acceptance + withdrawal note
	 * (shown once a payment method is set). false → plain imperative prompt with
	 * no checkbox (shown before a method is chosen).
	 */
	interactive: boolean;
	checked: boolean;
	onToggle: () => void;
	legalInfo: IOperatorLegalInfo;
}

export const TosAcceptance: FC<ITosAcceptanceProps> = ({
	interactive,
	checked,
	onToggle,
	legalInfo,
}) => {
	const { t } = useTranslation('ticketshop');

	const linkSx = {
		color: (theme: Theme) => theme.palette.primary.main,
		textDecorationColor: (theme: Theme) => theme.palette.primary.main,
	} as const;

	// Open the link without toggling the surrounding checkbox label.
	const stopToggle = (e: MouseEvent) => e.stopPropagation();

	const transComponents = {
		agb: (
			<Link
				href={legalInfo.agbUrl}
				target="_blank"
				rel="noopener noreferrer"
				underline="always"
				onClick={stopToggle}
				sx={linkSx}
			/>
		),
		cancellation: (
			<Link
				href={legalInfo.cancellationUrl}
				target="_blank"
				rel="noopener noreferrer"
				underline="always"
				onClick={stopToggle}
				sx={linkSx}
			/>
		),
	};

	if (!interactive) {
		return (
			<Typography variant="l1" color="solutions.600" sx={{ width: '100%' }}>
				<Trans
					i18nKey="ticketshop:payment.tos_prompt"
					values={{ operator: legalInfo.legalName }}
					components={transComponents}
				/>
			</Typography>
		);
	}

	return (
		<Stack gap={1} sx={{ width: '100%' }}>
			<Checkbox
				labelPlacement="start"
				checked={checked}
				onChange={onToggle}
				checkboxTestId="operator-ticket-payment-tos-checkbox"
				sx={{ alignItems: 'flex-start' }}
				label={
					<Typography variant="l1" color="solutions.600">
						<Trans
							i18nKey="ticketshop:payment.tos_acceptance"
							values={{ operator: legalInfo.legalName }}
							components={transComponents}
						/>
					</Typography>
				}
			/>

			<Typography variant="l1" color="solutions.600">
				{t('payment.withdrawal_note')}
			</Typography>
		</Stack>
	);
};
